// Shared "turn a business name + email into a Stripe Checkout redirect" path.
//
// Both entry points use it: the signup form and the homepage conversation.
// Neither one handles card details — Checkout does — so the only work here is
// reserving a slug and asking the API for the hosted Checkout URL.

import { apiFetch } from "../../lib/api";
import type { BillingCycle } from "../sections/Pricing";

/** How many `-2`, `-3` … suffixes to try before giving up on a taken name. */
const MAX_SLUG_ATTEMPTS = 5;

export function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 32);
}

/**
 * Slug rules are enforced by the API (2–40 chars, no leading/trailing hyphen).
 * Trimming to 32 chars above can leave a trailing hyphen, and a name made
 * entirely of punctuation leaves nothing at all.
 */
export function slugCandidate(name: string, attempt: number): string {
  const base = suggestSlug(name).replace(/^-+|-+$/g, "") || "site";
  const suffixed = attempt === 0 ? base : `${base}-${attempt + 1}`;
  return suffixed.length < 2 ? `${suffixed}-site` : suffixed;
}

export type StartTrialResult =
  | { ok: true; url: string }
  | { ok: false; error: string };

export async function startTrial(input: {
  businessName: string;
  email: string;
  cycle: BillingCycle;
}): Promise<StartTrialResult> {
  for (let attempt = 0; attempt < MAX_SLUG_ATTEMPTS; attempt += 1) {
    const slug = slugCandidate(input.businessName, attempt);
    const signup = await postSignup({ ...input, slug });

    if (!signup.ok) {
      if (signup.slugTaken) continue;
      return { ok: false, error: signup.error };
    }

    return postCheckout(signup.slug);
  }

  return {
    ok: false,
    error: "That name is taken. Try the signup form to pick your own address.",
  };
}

async function postSignup(input: {
  businessName: string;
  email: string;
  slug: string;
  cycle: BillingCycle;
}): Promise<
  { ok: true; slug: string } | { ok: false; error: string; slugTaken: boolean }
> {
  try {
    const r = await apiFetch("/signup", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "Idempotency-Key": `web:${crypto.randomUUID()}`,
      },
      body: JSON.stringify({
        businessName: input.businessName,
        slug: input.slug,
        email: input.email,
        billingCycle: input.cycle,
      }),
    });
    if (r.ok) {
      const data = (await r.json()) as { slug: string };
      return { ok: true, slug: data.slug };
    }
    const body = await r.text();
    return {
      ok: false,
      error: body || `Signup failed (${r.status})`,
      slugTaken: r.status === 409 && body.includes("slug_taken"),
    };
  } catch (e) {
    return { ok: false, error: (e as Error).message, slugTaken: false };
  }
}

async function postCheckout(slug: string): Promise<StartTrialResult> {
  try {
    const r = await apiFetch("/checkout/subscription", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "Idempotency-Key": `web:${crypto.randomUUID()}`,
      },
      body: JSON.stringify({ slug }),
    });
    if (!r.ok) {
      const body = await r.text();
      return { ok: false, error: body || `Checkout failed (${r.status})` };
    }
    const data = (await r.json()) as { url?: string };
    if (!data.url) return { ok: false, error: "missing_checkout_url" };
    return { ok: true, url: data.url };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}
